import type { ReactChild, SVGProps } from 'react';

import { Typography } from '@capsule/consumer-design-system';

import { motion } from 'framer-motion';
import { useHistory } from 'react-router';
import { css } from 'styled-components';

import { LAST_ANIMATION_DELAY } from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/constants';
import { SimpleFeatureLayout } from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/SimpleFeatureLayout';
import type { OtherMedsFormValues } from 'src/app/components/Pages/EscriptOnboarding/steps/OtherMedsYes';
import {
  antiobioticsOption,
  otherMedicationFormInitialFormValues,
  otherMedicationsOptions,
} from 'src/app/components/Pages/EscriptOnboarding/steps/OtherMedsYes';
import { EscriptOnboardingStepName } from 'src/app/components/Pages/EscriptOnboarding/types';
import { oxfordCommaFormatter, persistedFormValue } from 'src/app/components/Pages/EscriptOnboarding/utils';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { newOnboardingStep } from 'src/app/router/paths';
import { ESCRIPT_ONBOARDING_OTHER_MEDS_FORM } from 'src/app/storage/localStorageKeys';

const Bold = ({ children }: { children: ReactChild }) => (
  <Typography as="span" variant="heading4">
    {children}
  </Typography>
);

const mixBlendMultiply = css`
  mix-blend-mode: multiply;
`;

export const PillBottleArt = ({ className }: Pick<SVGProps<SVGSVGElement>, 'className'>) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 375 283" className={className}>
    <motion.g
      id="pill-bottle-illustration"
      animate={{ rotate: [0, -5, -5, 0] }}
      transition={{ type: 'spring', duration: 1, repeat: Infinity, repeatDelay: 1, delay: LAST_ANIMATION_DELAY }}
    >
      <path fill="#fff" d="M0 0h375v283H0z" />
      <circle cx="188" cy="141" r="105" fill="#99BFBC" />
      <path
        fill="#DFECEA"
        fillRule="evenodd"
        d="M137.9 64.4a3 3 0 0 0-2.57 3.38l5.37 39.53a3 3 0 0 0 3.38 2.57l9.9-1.34a9.16 9.16 0 0 1-3.3 4.81l-2.5 1.89a12.38 12.38 0 0 0-4.83 11.56l12.8 94.14a15 15 0 0 0 16.87 12.85l59.42-8.07a15 15 0 0 0 12.84-16.89l-12.79-94.14a12.38 12.38 0 0 0-7.73-9.86l-2.92-1.14a9.17 9.17 0 0 1-4.47-3.76l9.9-1.35a3 3 0 0 0 2.57-3.38l-5.37-39.53a3 3 0 0 0-3.37-2.57l-83.2 11.3Z"
        clipRule="evenodd"
        css={mixBlendMultiply}
      />
      <path
        fill="#0F3C6C"
        d="M138.12 122.3a12.38 12.38 0 0 1 4.82-11.56l2.51-1.9a9.17 9.17 0 0 0 3.57-8.56 9.17 9.17 0 0 1 7.86-10.32l43.67-5.93a9.17 9.17 0 0 1 10.32 7.85 9.18 9.18 0 0 0 5.73 7.3l2.93 1.15a12.38 12.38 0 0 1 7.73 9.86l12.78 94.08a15.06 15.06 0 0 1-12.9 16.95l-59.29 8.06a15.06 15.06 0 0 1-16.95-12.9l-12.78-94.08Z"
      />
      <path
        fill="#F8FBFB"
        d="m139.46 132.16 2.82-.39a10 10 0 0 1 11.26 8.57l7.69 56.58a10 10 0 0 1-8.57 11.26l-2.82.38-10.38-76.4ZM164.45 138.85a10 10 0 0 1 8.57-11.25l55.58-7.55 10.38 76.4L183.4 204a10 10 0 0 1-11.26-8.56l-7.69-56.59Z"
      />
      <rect
        width="49.6"
        height="6.43"
        x="174.3"
        y="143.17"
        fill="#D9D9D9"
        rx="3.21"
        transform="rotate(-8 174.3 143.17)"
      />
      <path
        fill="#DFECEA"
        d="m175.79 154.09 56.4-7.66 2.83 20.92-56.39 7.66zM143.04 158.54l6.37-.87 2.84 20.92-6.37.87z"
      />
      <path fill="#74A6A2" d="M194.23 189.43a1.94 1.94 0 0 0-.54-3.84l-3.29.47-3.3 4.38 7.13-1.01Z" />
      <path fill="#FFD300" d="M183.28 187.06a1.94 1.94 0 0 0 .54 3.84l3.29-.46 3.3-4.38-7.13 1Z" />
      <path fill="#0F3C6C" d="M191.4 193.18a1.94 1.94 0 1 1-3.83.55l-.46-3.3 3.3-4.37 1 7.12Z" />
      <path fill="#EF4931" d="M186.1 183.31a1.94 1.94 0 0 1 3.84-.54l.46 3.29-3.3 4.38-1-7.13Z" />
      <path
        fill="#F8FBFB"
        d="M130.1 63.28a3 3 0 0 1 2.57-3.38l83.2-11.3a3 3 0 0 1 3.37 2.57l5.37 39.53a3 3 0 0 1-2.57 3.38l-83.19 11.3a3 3 0 0 1-3.38-2.57l-5.37-39.53Z"
      />
    </motion.g>

    <path
      fill="#DFECEA"
      d="m121.88 105.56-28.06-8.98a3.79 3.79 0 0 0-4.67 2.18l-11.17 27.26a3.82 3.82 0 0 0 1.3 4.53l36.1 26.24a3.8 3.8 0 0 0 6.03-2.8l3.11-44.54a3.82 3.82 0 0 0-2.64-3.9"
      css={mixBlendMultiply}
    />
    <path
      fill="#FFD300"
      d="m116.88 100.56-28.06-8.98a3.79 3.79 0 0 0-4.67 2.18l-11.17 27.26a3.82 3.82 0 0 0 1.3 4.53l36.1 26.24a3.8 3.8 0 0 0 6.03-2.8l3.11-44.54a3.82 3.82 0 0 0-2.64-3.9"
    />
    <path
      fill="#DFECEA"
      d="M296.52 123.44c8.03 5.22 6.26 22.19-3.95 37.91-10.21 15.73-25 24.25-33.03 19.03-8.03-5.22-6.26-22.19 3.95-37.91 10.2-15.73 25-24.24 33.03-19.03Z"
      css={mixBlendMultiply}
    />
    <path
      fill="#EF4931"
      d="M292.52 117.44c8.03 5.22 6.26 22.19-3.95 37.91-10.21 15.73-25 24.25-33.03 19.03-8.03-5.22-6.26-22.19 3.95-37.91 10.2-15.73 25-24.24 33.03-19.03Z"
    />
  </svg>
);

/** These values won't have the word "medication" appended to them. */
const itemsRenderedWithoutSuffix = [antiobioticsOption, 'Birth control', 'Vitamin supplements'];

const formatOtherMedicationsFormValues = (selectedFormValues: OtherMedsFormValues): string => {
  const selection = otherMedicationsOptions.filter(med => !!selectedFormValues[med]);

  const numberOfSelected = selection.length;
  const isOneItem = numberOfSelected === 1;

  const didChooseAllOptions = otherMedicationsOptions.length === numberOfSelected;
  const hasOnlySelectedOthers = isOneItem && selection[0] === 'Others';

  if (didChooseAllOptions || hasOnlySelectedOthers || numberOfSelected >= 5) {
    /**
     * We don't want to return too long of a dynamic sentence. In these conditions, we simply
     * say: "All doctors; all meds".
     */
    return '';
  }

  const lastItem = selection[numberOfSelected - 1];

  // Basically, we append the value with the word "medication" if it makes the sentence sounds off.
  const formattedLastItem = (
    lastItem === 'Others'
      ? 'other medication'
      : itemsRenderedWithoutSuffix.includes(lastItem)
      ? lastItem
      : `${lastItem} medication`
  )
    .replace(antiobioticsOption, 'antiobiotics')
    .toLowerCase();

  if (isOneItem) {
    return formattedLastItem;
  }

  const firstItems = selection
    .filter(item => item !== lastItem)
    .map(item => (itemsRenderedWithoutSuffix.includes(item) ? item : `${item} meds`))
    .map(item => {
      if (item === antiobioticsOption) {
        return 'antibiotics';
      } else {
        return item.toLowerCase();
      }
    });
  return oxfordCommaFormatter([...firstItems, formattedLastItem]);
};

export const EscriptOnboardingAllDoctorsAllMeds = () => {
  useSegmentEvent('viewOtherMedicationsConfirmation', { shouldTrackOnMount: true });
  const history = useHistory();
  const { initialValue } = persistedFormValue(ESCRIPT_ONBOARDING_OTHER_MEDS_FORM, otherMedicationFormInitialFormValues);

  const otherMedications = initialValue ? formatOtherMedicationsFormValues(initialValue) : '';

  return (
    <SimpleFeatureLayout
      keyName={EscriptOnboardingStepName.any_doctor_any_med}
      title={`Great, you can get all your medications from Capsule`}
      description={
        !!otherMedications ? (
          <>
            Capsule works with <Bold>all</Bold> your doctors to fill <Bold>all</Bold> your prescriptions, including your{' '}
            {otherMedications}.
          </>
        ) : (
          <>
            Capsule works with <Bold>all</Bold> your doctors to fill <Bold>all</Bold> your prescriptions.
          </>
        )
      }
      body={{
        content: <PillBottleArt />,
        type: 'artwork',
      }}
      cta1={{
        children: 'Continue',
        onClick: () => history.push(newOnboardingStep.toPath({ stepName: EscriptOnboardingStepName.manage_your_meds })),
      }}
    />
  );
};
